import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatChildEmail } from '../lib/supabase';
import {
  Page,
  H1,
  P,
  Button,
  TextField,
  Box,
  Card,
  CardContent,
  Stack,
  Alert,
} from '../components';
import { ArrowBack, ChildCare } from '@mui/icons-material';

const ChildrenLoginPage = () => {
  const navigate = useNavigate();
  const { signInWithEmail } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [guardianCode, setGuardianCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChildLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!username.trim() || !password.trim() || !guardianCode.trim()) {
      setError('All fields are required');
      setLoading(false);
      return;
    }

    try {
      // Format the email using the guardian code and username
      const email = formatChildEmail(
        username.trim().toLowerCase(),
        guardianCode.trim().toLowerCase()
      );

      const { error } = await signInWithEmail(email, password);

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError(
            'Invalid username, password, or guardian code. Please check your details and try again.'
          );
        } else {
          setError(error.message);
        }
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page maxWidth="sm" fullHeight>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="80vh"
        gap={3}
      >
        <Button
          component={RouterLink}
          to="/login"
          startIcon={<ArrowBack />}
          sx={{ alignSelf: 'flex-start', mb: 2 }}
        >
          Back to Login Options
        </Button>

        <ChildCare
          sx={{
            fontSize: 64,
            color: 'secondary.main',
            mb: 1,
          }}
        />

        <H1>Children Login</H1>
        <P color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
          Enter your username, password, and guardian code to access your
          learning activities
        </P>

        <Card elevation={2} sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleChildLogin}>
              <Stack spacing={2}>
                <TextField
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  fullWidth
                  disabled={loading}
                  placeholder="Enter your username"
                  helperText="Your unique username (no spaces or special characters)"
                />

                <TextField
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  disabled={loading}
                  placeholder="Enter your password"
                />

                <TextField
                  label="Guardian Code"
                  value={guardianCode}
                  onChange={(e) => setGuardianCode(e.target.value)}
                  required
                  fullWidth
                  disabled={loading}
                  placeholder="Enter your guardian's code"
                  helperText="Ask your guardian for this special code"
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </Stack>
            </Box>

            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: 'info.light',
                borderRadius: 1,
              }}
            >
              <P
                variant="body2"
                color="info.contrastText"
                sx={{ textAlign: 'center' }}
              >
                💡 <strong>Need help?</strong> Ask your guardian for your
                username, password, and guardian code.
              </P>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Page>
  );
};

export default ChildrenLoginPage;
